import { watch, onScopeDispose } from 'vue'
import { ElNotification } from 'element-plus'
import { useWebSocketStore } from '@/store/modules/websocket'
import { useUserStore } from '@/store/modules/user'
import { getPrintScope, getPrinterBridge, type PrintScope } from './printRules'
import { runChargeDocument } from './chargePrint'
import { runPharmacyDocument } from './pharmacyPrint'
import { createPrintMessageQueue, parsePrintMessages, type AutoPrintConfig, type PrintMessage } from './printMessage'

// Mounted in the application layout, not a page: switching between pharmacy and
// Mounted in the application layout, not a page: switching pages does not stop receiving messages.
export function useChargePrintMessages() {
  const user = useUserStore()
  const socket = useWebSocketStore()
  let alive = true
  const sessions: Record<PrintScope, { scope: string; receive: ReturnType<typeof createPrintMessageQueue> } | undefined> = {
    charge: undefined,
    pharmacy: undefined,
  }
  onScopeDispose(() => { alive = false })
  watch(() => [user.getToken, getPrintScope('charge'), getPrintScope('pharmacy')], () => {
    sessions.charge = undefined
    sessions.pharmacy = undefined
  }, { flush: 'sync' })
  watch(() => socket.data, raw => {
    for (const message of parsePrintMessages(raw)) {
      const page = message.scope
      // 本机未登录该菜单时不打印对应单据。
      if (!user.checkMeunId(page === 'charge' ? '/charge' : '/pharmacy')) continue
      if (!getPrinterBridge()) continue
      const scope = getPrintScope(page)
      if (!scope) continue
      const token = user.getToken
      if (sessions[page]?.scope !== scope) {
        sessions[page] = {
          scope,
          receive: createPrintMessageQueue({
            readHistory: () => readHistory(scope),
            writeHistory: rows => localStorage.setItem(historyKey(scope), JSON.stringify(rows)),
            isActive: () => alive && user.getToken === token && getPrintScope(page) === scope,
            execute: (event, config) => {
              const output = config.printerName
                ? { deviceName: config.printerName, copies: config.printCopies, preview: config.previewEnabled }
                : undefined
              if (!output) return Promise.reject(new Error('后端未配置打印机'))
              return executeDocument(event, config, output)
            },
            reportError: (config, error) => ElNotification.error({
              title: config ? `${config.documentName}自动打印失败` : '自动打印失败',
              message: `${error instanceof Error ? error.message : '未知错误'}，请检查后手动补打`,
              duration: 0,
            }),
          }),
        }
      }
      sessions[page]!.receive(message)
    }
  }, { flush: 'sync' })
}

const historyKey = (scope: string) => `his:print-events:v1:${scope}`

const readHistory = (scope: string) => {
  const rows = JSON.parse(localStorage.getItem(historyKey(scope)) || '[]')
  if (!Array.isArray(rows)) throw new Error('打印记录损坏，请联系管理员')
  return rows
}

// 收费按收费单据执行，药房按药房单据执行，两者单据集合不同。
const executeDocument = (event: PrintMessage, config: AutoPrintConfig, output: { deviceName: string; copies: number; preview: boolean }) => {
  const id = event.businessId
  return event.scope === 'charge'
    ? runChargeDocument(id, config.documentType as Parameters<typeof runChargeDocument>[1], output, event.trigger === 'onPaid')
    : runPharmacyDocument(id, config.documentType as Parameters<typeof runPharmacyDocument>[1], output)
}
